import { FormCommonApiService } from '../../../../../service/api/formcommon-api.service';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DashboardService } from '../../../../../service/events/dashboard.service';
import { BaseForm } from '../../../../base-form';
import { ProductosApiService } from '../../../../../service/api/productos-api';
import { ProductosFitosanitariosFormDefinition } from './form-control/form-definition';
import { Utils } from '../../../../../common/utils';
import moment from 'moment';

import { ProductosModel } from '../../../../../models/productos/productos.model';
import { ColInterface } from '../../../../../common/components/common-list-table/col.interface';
import { ColStylesInterface } from '../../../../../common/components/common-list-table/col-styles.interface';
import { TreeNode } from 'primeng/api';
import { OtrasDenominacionesApiService } from '../../../../../service/api/otrasdenominaciones-api.service';
import { ReactiveFormRequestCoordinator } from '@fitogest/reactive-form-builder/form-request';
import { ProductCatalogCacheService } from 'src/app/service/cache/product-catalog-cache.service';
import { StorageManager } from 'src/app/common/storage-manager.class';
import { DropdownData, ReactiveFormStableValueChange, FormType } from '@fitogest/reactive-form-builder/interfaces';
import {
    baseRequest,
    baseSentence,
    getDropdownSelectedSingle,
    ReactiveCascadeController,
    ReactiveDropdownSyncController,
    setDropdownSelected,
} from '@fitogest/reactive-form-builder/utils';
import { RequestHandler } from 'src/app/service/OffService/request-handler';
import {
    buildProductosFitosanitariosProductPatch,
    getProductosFitosanitariosProductRegisterValue,
    getProductosFitosanitariosProductSearchTerm,
    getProductosFitosanitariosVademecumSearchTerm,
    isProductosFitosanitariosOtherDenominationEnabled,
    isProductosFitosanitariosProductModel,
    matchesProductosFitosanitariosProductIdentifier,
    resolveProductosFitosanitariosDropdownSource,
    resolveProductosFitosanitariosEditionSelection,
    buildProductosFitosanitariosSelectionResetPatch,
} from './producto-add-vademecum.util';

const PRODUCT_FORM_CACHE_TTL_MS = 300000;

@Component({
	selector: 'app-stock-formulario-productos',
	standalone: false,
	templateUrl: './productos-form.component.html',
	styleUrls: ['./productos-form.component.scss'],
})

export class ProductosFormComponent extends BaseForm<ProductosModel> implements OnInit, OnDestroy {
	public form: ProductosFitosanitariosFormDefinition = new ProductosFitosanitariosFormDefinition(
		this.getType(),
		this.getModel() as Record<string, string>,
		null,
	);

	public formRequest = new ReactiveFormRequestCoordinator<ProductosModel>();

	public vademecumTableRequest = new RequestHandler<ProductosModel[]>();

	public fecha_caducidad = '';

	public searchValue = {
		term: '',
	};

	private _dropdownsJustLoaded = false;

	private skipNextDenominationSourceSync = false;

    private vademecumSearchTimer: ReturnType<typeof setTimeout> | null = null;

	constructor(
		public dashboardEvents: DashboardService,
		public formApi: FormCommonApiService,
		public productosApi: ProductosApiService,
		public otrasDenominaciones: OtrasDenominacionesApiService,
		public override router: Router,
		public override route: ActivatedRoute,
		private readonly productCatalogCache: ProductCatalogCacheService,
	) {
		super(
			route,
			router,
			dashboardEvents,
			productosApi.productos.PUT,
			productosApi.productos.POST,
			'productos',
			'Actualizar Fitosanitario',
			'Alta de Producto Fitosanitario',
		);
		this.formRequest = new ReactiveFormRequestCoordinator<ProductosModel>();
	}

	ngOnInit(): void {
		this.resetExternalDropdowns();
		this.initializeHostModel(this.getModel() as ProductosModel);

		this.productosApi.productos.POST.reset();
		this.productosApi.productos.PUT.reset();

		this.initFormData();
		this.softInit(this.getType());
	}

	public stableValueChanges(event: ReactiveFormStableValueChange<ProductosModel>): void {
		const modelChanges = this.captureStableValueChange(event.value as ProductosModel);

		this.form.model = {
			...this.form.model,
			...modelChanges,
		};

		if (event.changedFields.includes('num_registro')) {
			this.handleRegisterNumberChange(event.value.num_registro);
		}

		if (event.changedFields.includes('nombre')) {
			this.setVademecumSearchValue(this.getVademecumSearchTerm());
		}

		if (event.changedFields.includes('otra_denominacion_comun')) {
			if (this.skipNextDenominationSourceSync) {
				this.skipNextDenominationSourceSync = false;
		} else {
				this.syncProductDropdownSource(true);
		}
		}
	}

	public productSelected(row: any): void {
		const product = (row?.data ?? row) as ProductosModel;

		if (!product) {
			return;
		}

		const patch = this.buildProductPatch(product);
		const productRegisterValue = this.getProductRegisterModelValue(product);

		setDropdownSelected(this.form.lists.productos, product);
		this.form.model = {
			...this.form.model,
			num_registro: productRegisterValue,
			...patch,
		};
		this.fecha_caducidad = product.fecha_caducidad ?? '';

		this.formBuilderRef.updateForm({
			num_registro: product,
			...patch,
		}, true);
	}

	public globalConditionalStyle(
		_cellValue: string,
		_colDefinition: ColInterface,
		_colGroup: ColInterface[],
		rowValue: TreeNode<ProductosModel>,
	): ColStylesInterface {
		const baseStyle: any = {
			color: 'black',
			backgroundColor: '#ffffff',
			borderBottom: '1px solid #dddddd',
		};

		const fechaCaducidad = Utils.toDate(rowValue.data?.fecha_caducidad ?? '');
		const fechaInscripcion = Utils.toDate(rowValue.data?.fecha_inscripcion ?? '');

		const diffCaducidad = moment().diff(fechaCaducidad, 'months');
		const diffInscripcion = moment().diff(fechaInscripcion, 'months');

		if (diffInscripcion < 12) {
			baseStyle.backgroundColor = '#cbdecb';
		}

		if (diffCaducidad > 12) {
			baseStyle.backgroundColor = '#222222';
			baseStyle.color = 'white';
		} else if (diffCaducidad > 6) {
			baseStyle.backgroundColor = '#ff7f7f';
		} else if (diffCaducidad >= 0) {
			baseStyle.backgroundColor = '#ffcf78';
		}

		return { rowStyle: baseStyle };
	}

	public globalVisualTransform(value: string): string {
		return value;
	}

    public override ngOnDestroy(): void {
        if (this.vademecumSearchTimer !== null) {
            clearTimeout(this.vademecumSearchTimer);
        }

        Utils.unsuscribe([
            this.formApi.magramaDetalleProducto,
        ]);

        Utils.unsuscribe([
            this.formApi.magramaProducto
        ]);
    }

	private initFormData(): Promise<boolean> {
		return Promise.all([
			this.productCatalogCache.getOrLoad<ProductosModel[]>(
				this.getProductCacheConfig('productos-vademecum'),
				() => baseRequest(this.formApi.productosVademecum)
					.then((data) => this.normalizeProductCatalog(data)),
			),
			this.productCatalogCache.getOrLoad<ProductosModel[]>(
				this.getProductCacheConfig('otras-denominaciones'),
				() => baseRequest(this.otrasDenominaciones.otrasDenominacionesGET)
					.then((data) => this.normalizeProductCatalog(data)),
			),
		])
			.then(([standardProducts, otherDenominations]) => {
				this.vademecumTableRequest.emitResponse(standardProducts);

				this.assignDropdownData(
					this.form.lists.denominaciones_estandar as DropdownData<ProductosModel>,
					standardProducts,
					(item: ProductosModel) => {
						const level = Utils.checkIfProductIsValid(item.fecha_caducidad, 'fitosanitario');

						return `${level} [${item.referencia}] ${item.nombre_comercial}`;
					},
				);

				this.assignDropdownData(
					this.form.lists.denominaciones_otras as DropdownData<ProductosModel>,
					otherDenominations,
					(item: ProductosModel) => `[${item.num_registro}] ${item.denominaciones_comunes} - ${item.nombre_comercial}`,
				);

				const useOtherCommonDenomination = this.isOtherCommonDenominationEnabled(this.getModel().otra_denominacion_comun);
				const dropdownSource = this.resolveProductDropdownSource(useOtherCommonDenomination);

				this.form.lists.productos = dropdownSource.dropdownData;
				this.form.model.otra_denominacion_comun = useOtherCommonDenomination ? '1' : '0';
				this.syncRegisterNumberFieldDropdown(dropdownSource.dropdownData, dropdownSource.valuePrimaryKey);

				this.finalizeExternalDropdowns();

				if ([FormType.EDITION, FormType.DUPLICATION].includes(this.getType())) {
					void this.restoreSelectedProductForEditionWhenReady();
				}

				return true;
			})
			.catch((error: unknown) => {
				console.error('Error loading product form dropdowns:', error);
				this.finalizeExternalDropdowns();

				return false;
			});
	}

	private normalizeProductCatalog(data: unknown): ProductosModel[] {
		if (Array.isArray(data)) {
			return data as ProductosModel[];
		}

		return data && typeof data === 'object' ? [data as ProductosModel] : [];
	}

	private assignDropdownData<T>(target: DropdownData<T>, source: T[], label: (item: T) => string): void {
		const options = baseSentence(source, label, true);

		target.filtered = [...options];
		target.values = [...options];
	}

	private async restoreSelectedProductForEditionWhenReady(): Promise<void> {
		await this.formBuilderRef.whenFieldsReady(['otra_denominacion_comun', 'num_registro']);
		this.restoreSelectedProductForEdition();
	}

	private restoreSelectedProductForEdition(): void {
		const selection = this.resolveSelectedProductForEdition();
		const dropdownSource = this.resolveProductDropdownSource(selection.useOtherCommonDenomination);
		const otherCommonDenominationControlValue = selection.useOtherCommonDenomination ? 1 : 0;
		const otherCommonDenominationModelValue = selection.useOtherCommonDenomination ? '1' : '0';

		this.form.lists.productos = dropdownSource.dropdownData;
		this.setOtherCommonDenominationModelValue(selection.useOtherCommonDenomination);
		this.syncRegisterNumberFieldDropdown(dropdownSource.dropdownData, dropdownSource.valuePrimaryKey);
		setDropdownSelected(dropdownSource.dropdownData, selection.product);

		if (selection.product === null) {
			this.form.model = {
				...this.form.model,
				num_registro: null,
		};
			this._dropdownsJustLoaded = true;
			this.skipNextDenominationSourceSync = true;
			this.formBuilderRef.updateForm({
				otra_denominacion_comun: otherCommonDenominationControlValue,
				num_registro: null,
		}, false);
			this.patchOtherCommonDenominationControl(selection.useOtherCommonDenomination);

			return;
		}

		const patch = this.buildProductPatch(selection.product);
		const productRegisterValue = this.getProductRegisterModelValue(selection.product);

		this.form.model = {
			...this.form.model,
			otra_denominacion_comun: otherCommonDenominationModelValue,
			num_registro: productRegisterValue,
			...patch,
		};
		this.fecha_caducidad = selection.product.fecha_caducidad ?? '';
		this._dropdownsJustLoaded = true;
		this.skipNextDenominationSourceSync = true;

		this.formBuilderRef.updateForm({
			otra_denominacion_comun: otherCommonDenominationControlValue,
			num_registro: selection.product,
			...patch,
		}, false);
		this.patchOtherCommonDenominationControl(selection.useOtherCommonDenomination);
	}

	private setOtherCommonDenominationModelValue(useOtherCommonDenomination: boolean): void {
		this.form.model = {
			...this.form.model,
			otra_denominacion_comun: useOtherCommonDenomination ? '1' : '0',
		};
	}

	private patchOtherCommonDenominationControl(useOtherCommonDenomination: boolean): void {
		this.setOtherCommonDenominationModelValue(useOtherCommonDenomination);
		this.formBuilderRef.updateForm({
			otra_denominacion_comun: useOtherCommonDenomination ? 1 : 0,
		}, false);
	}

	private resolveSelectedProductForEdition(): { product: ProductosModel | null; useOtherCommonDenomination: boolean } {
		return resolveProductosFitosanitariosEditionSelection({
			model: this.getModel(),
			standardProducts: this.getDropdownProducts(this.form.lists.denominaciones_estandar as DropdownData<ProductosModel>),
			otherDenominationProducts: this.getDropdownProducts(this.form.lists.denominaciones_otras as DropdownData<ProductosModel>),
		});
	}

	private getDropdownProducts(dropdownData: DropdownData<ProductosModel>): ProductosModel[] {
		return dropdownData.values
			.map((option) => option.raw ?? option.value)
			.filter(isProductosFitosanitariosProductModel);
	}


private getVademecumSearchTerm(): string {
    const selected = getDropdownSelectedSingle(this.form.lists.denominaciones_otras as DropdownData<ProductosModel>);
    const selectedOtherDenomination = selected !== null && !Array.isArray(selected) ? selected : null;

    return getProductosFitosanitariosVademecumSearchTerm({
        currentName: this.form.model.nombre ?? '',
        selectedOtherDenomination,
        useOtherCommonDenomination: this.isOtherCommonDenominationEnabled(),
    });
}


private handleRegisterNumberChange(value: unknown): void {
    if (this._dropdownsJustLoaded) {
        this._dropdownsJustLoaded = false;
        return;
    }

    const selectedProduct = this.getSelectedProduct(value);

    if (selectedProduct === null) {
        this.setVademecumSearchValue('');
        return;
    }

    const patch = this.buildProductPatch(selectedProduct);

    setDropdownSelected(this.form.lists.productos, selectedProduct);

    this.form.model = {
        ...this.form.model,
        ...patch,
    };
    this.fecha_caducidad = selectedProduct.fecha_caducidad ?? '';

    this.formBuilderRef.updateForm(patch, false);
    this.setVademecumSearchValue(this.getSelectedProductSearchTerm(selectedProduct));
}

private getSelectedProduct(value: unknown): ProductosModel | null {
    const resolved = this.formBuilderRef?.resolveDropdownRawValue<ProductosModel>('num_registro', value);

    if (resolved !== null && resolved !== undefined) {
        return resolved;
    }

    if (isProductosFitosanitariosProductModel(value)) {
        return value;
    }

    return this.getActiveProductDropdown().values
        .map((option) => option.raw ?? option.value)
        .find((product): product is ProductosModel => this.matchesProductIdentifier(product, value)) ?? null;
}

private buildProductPatch(product: ProductosModel): Pick<ProductosModel, 'nombre' | 'materia_activa'> {
    return buildProductosFitosanitariosProductPatch(product, this.isOtherCommonDenominationEnabled());
}

private getProductRegisterModelValue(product: ProductosModel): string | number {
    return getProductosFitosanitariosProductRegisterValue(product);
}

private getActiveProductDropdown(): DropdownData<ProductosModel> {
    return this.isOtherCommonDenominationEnabled()
        ? this.form.lists.denominaciones_otras as DropdownData<ProductosModel>
        : this.form.lists.denominaciones_estandar as DropdownData<ProductosModel>;
}

private matchesProductIdentifier(value: unknown, selectedValue: unknown): value is ProductosModel {
    return matchesProductosFitosanitariosProductIdentifier(value, selectedValue);
}

private getSelectedProductSearchTerm(product: ProductosModel): string {
    return getProductosFitosanitariosProductSearchTerm(product);
}


private setVademecumSearchValue(term: string): void {
    this.searchValue = {
        term: term.trim(),
    };
}

	private getProductCacheConfig(name: string): {
		namespace: string;
		name: string;
		ttlMs: number;
		userScope: string;
		clientScope: string;
	} {
		return {
			namespace: 'producto-add-form',
			name,
			ttlMs: PRODUCT_FORM_CACHE_TTL_MS,
			userScope: String(StorageManager.getUser()?.id ?? 'anonymous'),
			clientScope: String(StorageManager.getClient()?.id ?? 'no-client'),
		};
	}

    private syncProductDropdownSource(clearSelectedProduct: boolean): void {
        const dropdownSource = this.resolveProductDropdownSource(this.isOtherCommonDenominationEnabled());

        this.form.lists.productos = dropdownSource.dropdownData;

        if (clearSelectedProduct) {
            this.clearSelectedProductAfterDenominationChange();
        }

        ReactiveDropdownSyncController.syncFieldSource({
            formBuilder: this.formBuilderRef,
            sections: this.form.formFields.sections,
            fieldName: 'num_registro',
            dropdown: dropdownSource.dropdownData,
            valuePrimaryKey: dropdownSource.valuePrimaryKey,
            resetSelection: clearSelectedProduct,
        });
    }

    private syncRegisterNumberFieldDropdown(
        dropdownData: DropdownData<ProductosModel>,
        valuePrimaryKey: 'num_registro' | 'referencia',
    ): void {
        ReactiveDropdownSyncController.bindFieldData({
            field: ReactiveDropdownSyncController.findField<ProductosModel>(
                this.form.formFields.sections,
                'num_registro',
            ),
            dropdown: dropdownData,
            valuePrimaryKey,
        });
    }

    private clearSelectedProductAfterDenominationChange(): void {
        const { updates } = ReactiveCascadeController.createResetPatch<ProductosModel>([
            { fieldName: 'num_registro', value: null },
            { fieldName: 'nombre', value: '' },
            { fieldName: 'materia_activa', value: '' },
        ]);
        const resetPatch = {
            ...buildProductosFitosanitariosSelectionResetPatch(),
            ...updates,
        };

        this.fecha_caducidad = '';
        this.searchValue = { term: '' };
        this.form.model = {
            ...this.form.model,
            ...resetPatch,
        };
        this.formBuilderRef.updateForm(resetPatch, false);
    }

    private resolveProductDropdownSource(useOtherCommonDenomination: boolean): ReturnType<typeof resolveProductosFitosanitariosDropdownSource> {
        return resolveProductosFitosanitariosDropdownSource({
            standardProducts: this.form.lists.denominaciones_estandar as DropdownData<ProductosModel>,
            otherDenominationProducts: this.form.lists.denominaciones_otras as DropdownData<ProductosModel>,
            useOtherCommonDenomination,
        });
    }

    private isOtherCommonDenominationEnabled(value: unknown = this.form.model.otra_denominacion_comun): boolean {
        return isProductosFitosanitariosOtherDenominationEnabled(value);
    }


	override goBack(): void {
		window.history.back();
	}



	public get productoSeleccionado(): ProductosModel | null {
		return getDropdownSelectedSingle(this.form.lists.productos);
	}
}
